/**
 * 商品页 `.product_sticky_anchor`：随 Header 定位、滚动淡入淡出、Tab 锚点滚动。
 * 由 `product-information-custom` section 以 module 方式引入。
 */

const REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
const SCROLL_SHOW_THRESHOLD = 2;
const SCROLL_EXTRA_PADDING = 12;

/**
 * 获取顶部 Header 区域高度（与主题 `#header-group` / `header-component` 一致）。
 * @returns {number}
 */
function getHeaderHeight() {
  const headerGroup = document.querySelector('#header-group');
  if (headerGroup instanceof HTMLElement) {
    const h = Math.round(headerGroup.getBoundingClientRect().height);
    if (h > 0) return h;
  }
  const headerComponent = document.querySelector('#header-component, header-component');
  if (headerComponent instanceof HTMLElement) {
    const h = Math.round(headerComponent.getBoundingClientRect().height);
    if (h > 0) return h;
  }
  return 0;
}

/**
 * @param {string | null} selectorRaw `data-selector` 原始值（多为 id，无 `#`）
 * @returns {Element | null}
 */
function resolveScrollTarget(selectorRaw) {
  const raw = selectorRaw?.trim();
  if (!raw) return null;
  if (raw.startsWith('#') || raw.startsWith('.') || raw.startsWith('[')) {
    return document.querySelector(raw);
  }
  return document.getElementById(raw) ?? document.querySelector(`#${CSS.escape(raw)}`);
}

function initProductStickyAnchor() {
  const anchorEl = document.querySelector('.product_sticky_anchor');
  if (!(anchorEl instanceof HTMLElement)) return;
  const anchor = anchorEl;

  const stickyBar = document.querySelector('.sticky-add-to-cart__bar');

  anchor.style.transition = REDUCED_MOTION ? 'none' : 'opacity 0.35s ease';
  anchor.style.opacity = '0';
  anchor.style.pointerEvents = 'none';
  anchor.setAttribute('aria-hidden', 'true');

  const applyLayout = () => {
    const headerH = getHeaderHeight();
    const anchorH = anchor.offsetHeight;
    anchor.style.top = `${headerH}px`;
    if (stickyBar instanceof HTMLElement) {
      stickyBar.style.top = `${headerH + anchorH}px`;
    }
  };

  /** @param {boolean} visible */
  const setAnchorVisibility = (visible) => {
    if (visible) {
      anchor.style.opacity = '1';
      anchor.style.pointerEvents = '';
      anchor.setAttribute('aria-hidden', 'false');
    } else {
      anchor.style.opacity = '0';
      anchor.style.pointerEvents = 'none';
      anchor.setAttribute('aria-hidden', 'true');
    }
  };

  const syncScrollVisibility = () => {
    const y = window.scrollY || document.documentElement.scrollTop;
    setAnchorVisibility(y > SCROLL_SHOW_THRESHOLD);
  };

  let scrollRaf = 0;
  const onScroll = () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      syncScrollVisibility();
    });
  };

  anchor.querySelectorAll('.sticky_anchor_tab').forEach((tab) => {
    tab.addEventListener('click', (e) => {
      // e.preventDefault();
      anchor.querySelectorAll('.sticky_anchor_tab').forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');
    });
  });

  // 锚点链接滚动偏移 = product_sticky_anchor 高度 + sticky-add-to-cart__bar 高度 + 20
  const getAnchorOffset = () => {
    const anchorEl = document.querySelector('.product_sticky_anchor');
    const barEl = document.querySelector('.sticky-add-to-cart__bar');
    const aH = anchorEl instanceof HTMLElement ? anchorEl.offsetHeight : 0;
    const bH = barEl instanceof HTMLElement ? barEl.offsetHeight : 0;
    return aH + bH + 20;
  };
  // 导航锚点映射：稳定名称 → CSS选择器
  // 同一section被多次添加时，用索引区分（0=第一个, 1=第二个...）
  const navMap = {
    'r9-whole-body-support': { selector: '.r9_support_your_spine_container', index: 0 },
    'r9-precision-fit':      { selector: '.r9_precision_fit_container', index: 0 },
    'r9-free-hover':         { selector: '.r9_support_your_spine_container', index: 1 },
    'r9-adaptive-forward':   { selector: '.r9_designed_container', index: 0 },
    'r9-spc':                { selector: '.r9_specifications_container', index: 0 },
    'r9-about-us':           { selector: '.r9_who_we_are_container', index: 0 },
  };
  const nav = document.getElementById('productStickyAnchor') || document.querySelector('.product_sticky_anchor');
  if (nav) {
    nav.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        var name = (link.getAttribute('href') || '').replace('#', '');
        if (!name) return;
        var mapping = navMap[name];
        if (!mapping) return;
        var allSections = document.querySelectorAll(mapping.selector);
        var target = allSections[mapping.index];
        if (target) {
          var top = target.getBoundingClientRect().top + window.scrollY - getAnchorOffset();
          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
          });
        }
      });
    });
  }

  applyLayout();
  syncScrollVisibility();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', applyLayout);

  const headerGroup = document.querySelector('#header-group');
  if (headerGroup instanceof HTMLElement) {
    new ResizeObserver(applyLayout).observe(headerGroup);
  }
  new ResizeObserver(applyLayout).observe(anchor);

  const headerComponent = document.querySelector('#header-component, header-component');
  if (headerComponent instanceof HTMLElement && headerComponent !== headerGroup) {
    new ResizeObserver(applyLayout).observe(headerComponent);
  }

  document.fonts?.ready?.then(applyLayout).catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductStickyAnchor, { once: true });
} else {
  initProductStickyAnchor();
}

// sticky buy now (only handles new_buy_now_button)
(function() {
  document.addEventListener('click', async function(e) {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn || btn.disabled) return;

    // Only handle buttons with new_buy_now_button_text (not checkout_pay_now_button)
    const textEl = btn.querySelector('.new_buy_now_button_text');
    if (!textEl) return;

    e.preventDefault();
    e.stopPropagation();

    // Get product data from the sticky add to cart component
    const stickyCart = btn.closest('sticky-add-to-cart');
    if (!stickyCart) return;

    const variantId = stickyCart.dataset.currentVariantId;
    const quantity = parseInt(stickyCart.dataset.initialQuantity) || 1;

    if (!variantId) return;

    btn.disabled = true;

    // Add loading state to button
    const originalText = textEl.textContent;
    textEl.textContent = 'Pay Now...';

    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(function(data) {
      // Check if there was an error in adding to cart
      if (data.message) {
        // Show error message (you can customize this)
        console.error('Add to cart error:', data.message);
        alert('Failed to add item to cart: ' + data.message);
      } else {
        // Success - redirect to checkout
        window.location.href = '/checkout';
      }
    })
    .catch(function(error) {
      console.error('Buy Now error:', error);
      alert('Checkout failed. Please try again later');
    })
    .finally(function() {
      // Restore button state
      btn.disabled = false;
      textEl.textContent = originalText;
    });
  });
})();